import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import { inviteAdvisorByEmail } from '../../api/advisorInviteApi';
import {
  ADVISOR_CONSENT_DOC_PATH,
  LEGAL_DRAFT_MARKER,
} from '../../config/advisorConsentAccess';
import { useLegalDocument } from './useLegalDocument';
import AdvisorConsentDialog from './AdvisorConsentDialog';

/**
 * M11b-4 — Berater-Einladung per E-Mail (GATED, hinter
 * `REACT_APP_ADVISOR_EMAIL_INVITE_ENABLED`; nur gemountet, wenn das Flag on ist).
 *
 * Der Owner gibt die E-Mail-Adresse des Steuerberaters ein und bestätigt über eine
 * NICHT vorausgewählte Pflicht-Checkbox ([JURIST] J-a), dass er das Teilen seiner
 * Buchungsbelege mit dieser Adresse beauftragt. Erst dann wird der „Einladen"-Button
 * aktiv. Die eigentliche Auflösung (registriert → Direkt-Invite / nicht registriert →
 * Token-Invite-Mail) passiert server-seitig in der Callable {@link inviteAdvisorByEmail};
 * die Antwort ist bewusst UNIFORM (Anti-Enumeration) → die UI zeigt immer dieselbe
 * Erfolgsmeldung, unabhängig davon ob die Adresse registriert ist.
 *
 * Die harte Read-only-Durchsetzung (advisor schreibt nie) liegt weiterhin in den
 * Firestore-Rules + dem PHP-`requireWrite`-Guard. Die Client-Disable-Logik der
 * Checkbox ist nur UX — der Server erzwingt `ownerConfirmed===true` zusätzlich.
 *
 * Hinweis Rechtstexte: Die DE-Wortlaute (Checkbox `tenant.inviteOwnerConfirm`,
 * Mail-Text) sind JURIST-ENTWÜRFE und vor dem Flag-Flip vom Fachanwalt zu
 * finalisieren (J-g, legal-approvals.json).
 */
interface AdvisorInvitePanelProps {
  /**
   * Optional callback invoked after the owner successfully sends an invitation.
   * Callers (e.g. AdvisorPortalPage) can use this to refresh the sent-invites list.
   */
  onInviteSent?: () => void;
}

const AdvisorInvitePanel: React.FC<AdvisorInvitePanelProps> = ({ onInviteSent }) => {
  const { t } = useTranslation('common');
  const { currentUser } = useAuth();

  const [advisorEmail, setAdvisorEmail] = useState('');
  const [ownerConfirmed, setOwnerConfirmed] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // §3-Consent (B3): der Dialog wird nur DAZWISCHENGESCHALTET, wenn die §3-Datei
  // freigegeben (`ready`) ist. Fehlt/Draft (aktueller Zustand, M-OB-15 ausstehend)
  // ⇒ Bestandsverhalten (Direkt-Send ohne consentAccepted) — inert.
  const [showConsent, setShowConsent] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const consentDoc = useLegalDocument(ADVISOR_CONSENT_DOC_PATH, LEGAL_DRAFT_MARKER);

  const emailTrimmed = advisorEmail.trim();
  const canSubmit = !!currentUser && !!emailTrimmed && ownerConfirmed && !isInviting;

  /**
   * Sendet die Einladung. `viaConsent` unterscheidet den §3-Dialog-Pfad (sendet
   * `consentAccepted:true`) vom inerten Direkt-Pfad (Bestandsarität, kein 3. Arg —
   * hält bestehende Spec-Tests grün UND ist inert, solange die §3-Datei fehlt).
   */
  const doSend = async (viaConsent: boolean) => {
    setIsInviting(true);
    try {
      // Server-Antwort ist uniform ({status:'invited'}) → immer dieselbe Meldung.
      if (viaConsent) {
        await inviteAdvisorByEmail(emailTrimmed, ownerConfirmed, true);
      } else {
        await inviteAdvisorByEmail(emailTrimmed, ownerConfirmed);
      }
      setAdvisorEmail('');
      setOwnerConfirmed(false);
      setShowConsent(false);
      setConsentError(null);
      setStatus({ type: 'success', message: t('tenant.inviteSent') });
      // Notify parent so the sent-invites list can refresh (optional, M11b-5).
      onInviteSent?.();
    } catch (err) {
      logger.error('[AdvisorInvitePanel] Failed to invite advisor by email', err);
      if (viaConsent) {
        setConsentError(t('tenant.inviteError'));
      } else {
        setStatus({ type: 'error', message: t('tenant.inviteError') });
      }
    } finally {
      setIsInviting(false);
    }
  };

  const handleInvite = async () => {
    setStatus(null);

    if (!currentUser) {
      setStatus({ type: 'error', message: t('tenant.inviteError') });
      return;
    }
    if (!emailTrimmed) {
      setStatus({ type: 'error', message: t('tenant.inviteEmailMissing') });
      return;
    }
    if (!ownerConfirmed) {
      // Server enforces this too; this is the UX guard.
      setStatus({ type: 'error', message: t('tenant.inviteOwnerConfirmRequired') });
      return;
    }

    // §3-Consent-Gate: nur bei freigegebener Datei den Dialog erzwingen; sonst inert.
    if (consentDoc.kind === 'ready') {
      setConsentError(null);
      setShowConsent(true);
      return;
    }

    await doSend(false);
  };

  return (
    <div>
      <label htmlFor="advisor-email-input" className="block text-xs font-medium text-muted mb-1">
        {t('tenant.inviteEmailLabel')}
      </label>
      <input
        id="advisor-email-input"
        type="email"
        value={advisorEmail}
        onChange={(e) => setAdvisorEmail(e.target.value)}
        placeholder={t('tenant.inviteEmailPlaceholder')}
        disabled={isInviting}
        className="w-full rounded-md bg-surface border border-hairline px-2 py-1.5 text-sm text-primary placeholder:text-faint focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
      />

      {/* [JURIST] J-a — Pflicht-Bestätigung des Owners, NICHT vorausgewählt. */}
      <label className="mt-3 flex items-start gap-2 text-[12px] leading-relaxed text-secondary">
        <input
          type="checkbox"
          checked={ownerConfirmed}
          onChange={(e) => setOwnerConfirmed(e.target.checked)}
          disabled={isInviting}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong bg-surface text-indigo-600 focus:ring-indigo-500"
        />
        <span>{t('tenant.inviteOwnerConfirm')}</span>
      </label>

      <button
        type="button"
        onClick={handleInvite}
        disabled={!canSubmit}
        className="mt-3 inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isInviting ? t('tenant.inviting') : t('tenant.inviteButton')}
      </button>

      {status && (
        <p className={`mt-2 text-xs ${status.type === 'success' ? 'text-[var(--tone-success-fg)]' : 'text-[var(--tone-danger-fg)]'}`}>
          {status.message}
        </p>
      )}

      {/* Berater-Rechtehinweis (Jurist-Wortlaut, M11 W4). §-Verweise StBerG/StGB
          bleiben in allen Sprachen erhalten. */}
      <p className="mt-3 text-[11px] leading-relaxed text-faint">
        {t('tenant.inviteLegalNotice', {
          advisor: emailTrimmed || t('tenant.advisorFallback'),
        })}
      </p>

      {/* §3-Mandanten-Consent-Dialog (B3): kanzlei_name := eingegebene Empfänger-
          E-Mail (§5.6). Nur sichtbar, wenn die §3-Datei freigegeben ist. */}
      {showConsent && (
        <AdvisorConsentDialog
          kanzleiName={emailTrimmed}
          submitting={isInviting}
          error={consentError}
          onGrant={() => doSend(true)}
          onCancel={() => {
            setShowConsent(false);
            setConsentError(null);
          }}
        />
      )}
    </div>
  );
};

export default AdvisorInvitePanel;
